import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useCancelCommandMutation,
  useRetryCommandMutation,
  useRunCommandMutation,
  type Command,
  type CommandAction,
  type CommandListResponse,
} from '@/entities/command'
import { normalizeError } from '@/shared/api'
import { formatTime, showToast } from '@/shared/lib'
import { Alert } from '@/shared/ui/Alert'
import { Button } from '@/shared/ui/Button'
import { EmptyState } from '@/shared/ui/EmptyState'
import { Icon } from '@/shared/ui/Icon'
import { Modal } from '@/shared/ui/Modal'
import { Skeleton } from '@/shared/ui/Skeleton'
import { StatusBadge } from '@/shared/ui/StatusBadge'
import { CommandCard } from './CommandCard'
import { ExchangeChip } from './ExchangeChip'
import s from './ProcessMonitorModal.module.css'

const SKELETON_ROWS = [0, 1, 2]

type ProcessMonitorModalProps = {
  open: boolean
  onClose: () => void
  data: CommandListResponse | undefined
  isLoading: boolean
  isError: boolean
  error: unknown
  onRetry: () => void
}

/**
 * Модалка очереди 1С: список команд с действиями и подвал со связью с 1С и итогами за час. Данные приходят
 * от кнопки в шапке — тот же запрос, что и у неё; сама модалка только рисует состояния:
 * загрузка, «нет доступа», сбой с повтором, пусто, список.
 *
 * Действие карточки: кнопка ждёт ответа, остальные действия той же карточки гаснут; исход —
 * тостом, список перечитывает инвалидация тега. `409` — 1С уже ответила: список обновится сам.
 */
export function ProcessMonitorModal({
  open,
  onClose,
  data,
  isLoading,
  isError,
  error,
  onRetry,
}: ProcessMonitorModalProps) {
  const { t } = useTranslation('processes')
  const { t: tc } = useTranslation()
  const [cancel] = useCancelCommandMutation()
  const [retry] = useRetryCommandMutation()
  const [run] = useRunCommandMutation()
  const [pending, setPending] = useState<{ id: string; action: CommandAction } | null>(null)

  const isForbidden = isError && normalizeError(error).status === 403
  const summary = data?.summary
  const running = summary?.running ?? 0

  const act = async (command: Command, action: CommandAction) => {
    setPending({ id: command.id, action })
    try {
      if (action === 'cancel') await cancel(command.id).unwrap()
      else if (action === 'retry') await retry(command.id).unwrap()
      else await run(command.id).unwrap()
      showToast(t(`toast.${action}`), { tone: 'success' })
    } catch (failure) {
      const { status } = normalizeError(failure)
      // 1С уже ответила, действие потеряло смысл: тег инвалидирован, список подтянет итог
      showToast(t(status === 409 ? 'toast.conflict' : 'toast.failed'), {
        tone: status === 409 ? 'accent' : 'danger',
        announce: 'assertive',
      })
    } finally {
      setPending(null)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      heading={
        <span className={s.heading}>
          {t('modal.heading')}
          {running > 0 ? (
            <StatusBadge tone="accent" size="sm" indicator="spinner">
              {t('modal.running', { count: running })}
            </StatusBadge>
          ) : null}
        </span>
      }
      description={t('modal.description')}
      closeLabel={t('modal.close')}
      footer={
        <div className={s.footer}>
          <div className={s.meta}>
            {summary ? (
              <>
                <ExchangeChip exchange={summary.exchange} />
                <span className={s.summary}>
                  <span>{t('modal.failedCount', { count: summary.failed })}</span>
                  {' · '}
                  <span>{t('modal.doneLastHour', { count: summary.doneLastHour })}</span>
                  {' · '}
                  <span>
                    {summary.lastSyncAt !== null
                      ? t('modal.lastSync', { time: formatTime(summary.lastSyncAt) })
                      : t('modal.noSync')}
                  </span>
                </span>
              </>
            ) : null}
          </div>
          <Button variant="primary" size="md" onClick={onClose}>
            {t('modal.done')}
          </Button>
        </div>
      }
    >
      <div className={s.body} aria-busy={isLoading || undefined}>
        {isLoading ? (
          <ul className={s.list} aria-hidden="true">
            {SKELETON_ROWS.map((row) => (
              <li key={row} className={s.skeletonRow}>
                <Skeleton shape="line" size="md" width="medium" />
                <Skeleton shape="line" size="sm" width="long" />
                <Skeleton shape="block" size="sm" />
              </li>
            ))}
          </ul>
        ) : null}

        {isForbidden ? (
          <EmptyState
            heading={tc('errors.forbidden')}
            headingLevel={3}
            icon={<Icon name="scale" size="lg" />}
          >
            {t('modal.forbiddenText')}
          </EmptyState>
        ) : null}

        {isError && !isForbidden ? (
          <Alert
            tone="danger"
            heading={t('modal.errorHeading')}
            announce="polite"
            action={
              <Button size="sm" variant="secondary" onClick={onRetry}>
                {tc('action.retry')}
              </Button>
            }
          >
            {t('modal.errorText')}
          </Alert>
        ) : null}

        {!isLoading && !isError && data !== undefined && data.items.length === 0 ? (
          <EmptyState
            heading={t('modal.emptyHeading')}
            headingLevel={3}
            icon={<Icon name="clock" size="lg" />}
          >
            {t('modal.emptyText')}
          </EmptyState>
        ) : null}

        {!isLoading && !isError && data !== undefined && data.items.length > 0 ? (
          <ul className={s.list} aria-label={t('modal.listLabel')}>
            {data.items.map((command) => (
              <CommandCard
                key={command.id}
                command={command}
                pendingAction={pending?.id === command.id ? pending.action : null}
                onAction={(action) => void act(command, action)}
              />
            ))}
          </ul>
        ) : null}
      </div>
    </Modal>
  )
}
